import * as THREE from 'three';
import { between, smooth, type Random } from './critter-math.js';
import { bakeCritter, parts } from './critter-parts.js';

/** A butterfly is a few centimetres across; it is drawn at this multiple so it reads from the isometric camera. */
export const BUTTERFLY_SCALE = 3.2;
const TONES = [
  { wing: 0xe8d84a, edge: 0x8a7a1f }, { wing: 0xf1eee2, edge: 0x2a2a2a }, { wing: 0xe27a2c, edge: 0x2b1c12 }, { wing: 0x6d9fe0, edge: 0x1e2b52 },
];

/** A butterfly: a slim body and two pairs of wings that beat and open flat to glide. Wings are single-sided sheets seen from both faces. */
export class ButterflyModel {
  readonly root = new THREE.Group();
  private wings: THREE.Group[] = [];

  constructor(tone = 0) {
    const { wing, edge } = TONES[tone % TONES.length];
    parts.blob(this.root, 0x2b2420, [.006, .006, .03], [0, 0, 0]);
    parts.blob(this.root, 0x2b2420, [.008, .008, .008], [0, 0, .035]);
    for (const side of [-1, 1]) {
      const group = new THREE.Group(); group.position.set(side * .004, .004, 0); group.scale.x = side; this.root.add(group);
      const fore = parts.feathers(group, wing, .05, .06, .035, .01); fore.position.set(0, 0, .008);
      const tip = parts.feathers(group, edge, .014, .03, .02, .004); tip.position.set(.046, .001, .006);
      const hind = parts.feathers(group, wing, .038, .05, .04, -.008); hind.position.set(0, -.001, -.012);
      this.wings.push(group);
    }
    bakeCritter(this.root);
  }

  /** `beat` is the wingbeat phase in radians; `glide` 0–1 holds the wings open and still. */
  apply(beat: number, glide: number) {
    this.wings.forEach(wing => { // A mirrored wing turns the other way round, so its sign is folded in and both beat together.
      wing.rotation.z = (Math.sin(beat) * .95 * (1 - glide) + .35 * glide) * wing.scale.x; });
  }
}

/**
 * One butterfly flitting about a patch of flowers: it drifts in loose figures within a few metres, rising and dipping, in
 * bursts of fast wingbeats between short glides, heading wherever it is going. Position is a function of time alone.
 */
export class Flutterer {
  readonly position = new THREE.Vector3();
  heading = 0;
  readonly model: ButterflyModel;
  private readonly phase: number; private readonly reach: number; private readonly height: number; private readonly speed: number;
  private ahead = new THREE.Vector3(); private time = 0;

  constructor(private centre: { x: number; z: number }, random: Random, tone: number) {
    this.model = new ButterflyModel(tone); this.model.root.scale.setScalar(BUTTERFLY_SCALE * between(random, .85, 1.15));
    this.model.root.traverse(object => { object.castShadow = false; });
    this.phase = random() * 40; this.reach = between(random, 2, 4.5); this.height = between(random, .8, 1.8); this.speed = between(random, .5, .85);
    this.place(0, this.position);
  }

  private place(time: number, into: THREE.Vector3) {
    const t = time * this.speed + this.phase;
    return into.set(
      this.centre.x + Math.sin(t * .9) * this.reach + Math.sin(t * 2.3 + 1) * this.reach * .3,
      this.height + Math.sin(t * 1.7) * .3 + Math.sin(t * 5.1) * .08,
      this.centre.z + Math.cos(t * .73 + 1.3) * this.reach + Math.cos(t * 2.9) * this.reach * .25);
  }

  update(dt: number) {
    this.time += dt;
    this.place(this.time, this.position); this.place(this.time + .3, this.ahead);
    this.heading = Math.atan2(this.ahead.x - this.position.x, this.ahead.z - this.position.z);
    const glide = smooth(Math.sin(this.time * .8 + this.phase * 3) * 2.2 - .4);
    this.model.apply(this.time * 15 + this.phase, glide);
    this.model.root.position.copy(this.position); this.model.root.rotation.set(Math.sin(this.time * 1.3 + this.phase) * .2, this.heading, Math.sin(this.time * 2 + this.phase) * .25, 'YXZ');
  }
}
